import { Box, Divider, Flex, IconButton, Text, VStack } from '@chakra-ui/react'
import { ScreenContext, useAuth } from '@hb/react'
import { colors } from '@hb/shared'
import * as React from 'react'
import { FC, useContext, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import hamburgerIcon from '../../icons/hamburger-menu.svg'
import { View } from '../../types/views'
import { UserPanel } from '../Auth'
import { AccountButton } from '../Auth/AccountModal'
import { SiteSettingsButton } from '../Settings/SiteSettings'
import { useNestedViews } from './hooks'

const MenuContext = React.createContext<{ hideMenu: () => void }>({
  hideMenu: () => {},
})

export const getMenuLinkTo = (path: string | string[]) => {
  const toLinkItem = typeof path === 'string' ? path : path[0]
  return toLinkItem.startsWith('/:') ? '/' : toLinkItem
}
const MenuItem: React.FC<View> = ({ name, path }) => {
  const { hideMenu } = React.useContext(MenuContext)
  const toLink = getMenuLinkTo(path)
  return (
    <Link onClick={hideMenu} style={{ textDecoration: 'none', color: '#333' }} to={toLink}>
      <Flex fontSize="lg" fontFamily="Open Sans" px={2}>
        {name}
      </Flex>
    </Link>
  )
}

const MenuFolder: React.FC<{
  views: View[]
  name?: string
}> = ({ views, name }) =>
  views.length ? (
    <VStack
      w="100%"
      pt={2}
      pb={1}
      spacing={0}
      style={{
        alignItems: 'flex-start',
      }}>
      {name ? (
        <Text
          color={colors.indigo.hex}
          whiteSpace="nowrap"
          px={3}
          py={1}
          lineHeight={1}
          fontFamily="Comfortaa"
          fontWeight={600}
          fontSize="md">
          {name}
        </Text>
      ) : null}
      <Box px={2} w="100%">
        {views.map(v => (
          <MenuItem key={typeof v.path === 'string' ? v.path : v.path[0]} {...v} />
        ))}
      </Box>
    </VStack>
  ) : null

export const Menu: FC<{ views: View[] }> = ({ views }) => {
  const [active, setActive] = useState(false)
  const { isMobile, width: screenWidth } = useContext(ScreenContext)
  const width = useMemo(() => Math.min(screenWidth - 60, 325), [screenWidth])
  const authUser = useAuth(s => s.authUser)
  const { publicViews, userViews, adminViews, superAdminViews } = useNestedViews(views)
  if (!isMobile) return null
  return (
    <MenuContext.Provider value={{ hideMenu: () => setActive(false) }}>
      <Flex
        display={isMobile ? 'flex' : 'none'}
        direction="row"
        position="fixed"
        w="100%"
        h="100%"
        zIndex={3}
        onClick={() => setActive(false)}
        style={{
          pointerEvents: active ? 'auto' : 'none',
          flexFlow: 'row',
          background: `rgba(0,0,0,${active ? 0.3 : 0})`,
          transition: 'background 500ms',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}>
        <VStack
          pos="absolute"
          right={`${active ? 0 : -(width + 5)}px`}
          spacing={0}
          transition="right 300ms"
          boxShadow="0 0 4px #555"
          borderLeftRadius={6}
          bg="white"
          _dark={{ bg: 'gray.900' }}
          width={`${width}px`}
          maxH="100%">
          <VStack
            opacity={active ? 1 : 0}
            transition="all 300ms"
            spacing={0}
            w="100%"
            height="100%"
            overflowY="auto">
            <VStack divider={<Divider />} spacing={0} w="100%" flex={1}>
              <MenuFolder name="Hamilton Billing" views={publicViews} />
              <MenuFolder name="My Account" views={userViews} />
              <MenuFolder name="Admin Access" views={adminViews} />
              <MenuFolder name="Super Admin Access" views={superAdminViews} />
              {authUser ? (
                <>
                  <SiteSettingsButton />
                  <AccountButton />
                </>
              ) : null}
              <UserPanel />
            </VStack>
            {/* <UserPanel /> */}
          </VStack>
          <IconButton
            aria-label="Open menu"
            onClick={e => {
              setActive(!active)
              e.stopPropagation()
            }}
            variant="unstyled"
            position="absolute"
            left="-46px"
            transition="left 300ms"
            pointerEvents="auto"
            alignItems="center"
            bg="transparent"
            top={2}
            icon={
              <img style={{ width: 30, filter: 'drop-shadow(0 0 1px #111)' }} src={hamburgerIcon} />
            }
          />
        </VStack>
      </Flex>
    </MenuContext.Provider>
  )
}

export default Menu
